import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import { useEffect, useState } from "react";
import { FiMenu, FiScissors, FiUser, FiX, FiLogOut, FiChevronDown, FiMonitor, FiBook, FiTrendingUp, FiCpu, FiUploadCloud, FiZap, FiHome } from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAuthActions } from "../../hooks/useAuthActions";

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<{ username?: string; email?: string } | null>(null);
  const { isAuthenticated, logout } = useAuth();
  const { getLoggedInUser } = useAuthActions();
  const location = useLocation();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await getLoggedInUser();
        setUser(data);
      } catch (err) {
        console.log(err);
      }
    };
    if (isAuthenticated) fetchUser();
    else setUser(null);
  }, [isAuthenticated, getLoggedInUser]);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const aiHubLinks = [
    { to: "/vision-studio", icon: <FiMonitor />, label: "Vision Studio", color: "#4F46E5", desc: "AI Document Chat" },
    { to: "/edu-studio", icon: <FiBook />, label: "Edu Studio", color: "#059669", desc: "Knowledge Kits" },
    { to: "/career-sync", icon: <FiTrendingUp />, label: "Career Sync", color: "#0EA5E9", desc: "AI Interviewer" },
    { to: "/architect-studio", icon: <FiCpu />, label: "Architect Studio", color: "#E11D48", desc: "Blueprint Builder" },
  ];

  const toolLinks = [
    { to: "/pdf-compressor", icon: <FiScissors />, label: "PDF Shrink", color: "#4F46E5", desc: "Compress PDFs" },
    { to: "/uploader", icon: <FiUploadCloud />, label: "Image Optimizer", color: "#0EA5E9", desc: "Optimize Images" },
  ];

  return (
    <>
      <nav
        className="navbar navbar-expand-lg fixed-top py-2"
        style={{
          zIndex: 1050,
          background: "rgba(2, 6, 23, 0.95)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
      >
        <div className="container">
          {/* Logo */}
          <Link className="navbar-brand d-flex align-items-center gap-2 text-decoration-none" to="/">
            <div
              style={{
                background: "linear-gradient(135deg, #4F46E5, #E11D48)",
                padding: "8px",
                borderRadius: "12px",
                display: "flex",
                boxShadow: "0 4px 15px rgba(79, 70, 229, 0.5)",
              }}
            >
              <FiScissors size={22} color="white" />
            </div>
            <span
              className="fw-900"
              style={{
                fontSize: "1.4rem",
                letterSpacing: "-1px",
                color: "white",
              }}
            >
              Smart<span style={{ color: "#818CF8" }}>Shrink</span>
            </span>
          </Link>

          {/* Mobile Toggle */}
          <button
            className="navbar-toggler border-0 shadow-none"
            style={{ zIndex: 1100 }}
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.1)",
                borderRadius: "10px",
                padding: "8px",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              {isMenuOpen ? <FiX size={22} color="white" /> : <FiMenu size={22} color="white" />}
            </div>
          </button>

          {/* Desktop Menu */}
          <div className={`collapse navbar-collapse ${isMenuOpen ? "show" : ""}`}>
            <ul className="navbar-nav ms-auto align-items-center gap-1">

              {/* Home */}
              <li className="nav-item">
                <Link
                  to="/"
                  className="nav-link fw-700 d-flex align-items-center gap-2 px-3 py-2 rounded-3"
                  style={{
                    color: isActive("/") ? "#818CF8" : "rgba(255,255,255,0.75)",
                    background: isActive("/") ? "rgba(79,70,229,0.15)" : "transparent",
                    transition: "all 0.25s ease",
                    fontSize: "0.9rem",
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FiHome size={15} /> Home
                </Link>
              </li>

              {/* AI Hub Dropdown */}
              <li className="nav-item dropdown">
                <a
                  className="nav-link fw-700 dropdown-toggle d-flex align-items-center gap-2 px-3 py-2 rounded-3"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{
                    color: "rgba(255,255,255,0.75)",
                    transition: "all 0.25s ease",
                    fontSize: "0.9rem",
                  }}
                >
                  <FiZap size={15} /> AI Hub
                </a>
                <ul
                  className="dropdown-menu border-0 p-3 mt-2"
                  style={{
                    background: "rgba(10, 15, 30, 0.97)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "18px",
                    boxShadow: "0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)",
                    minWidth: "240px",
                  }}
                >
                  {aiHubLinks.map((item) => (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className="dropdown-item rounded-3 py-2 d-flex align-items-center gap-3 fw-bold"
                        style={{ color: "rgba(255,255,255,0.85)", transition: "all 0.2s" }}
                        onClick={() => setIsMenuOpen(false)}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.07)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <div
                          style={{
                            background: `${item.color}22`,
                            color: item.color,
                            padding: "8px",
                            borderRadius: "10px",
                            display: "flex",
                          }}
                        >
                          {item.icon}
                        </div>
                        <div>
                          <div style={{ fontSize: "0.9rem" }}>{item.label}</div>
                          <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", fontWeight: 500 }}>{item.desc}</div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>

              {/* Tools Dropdown */}
              <li className="nav-item dropdown">
                <a
                  className="nav-link fw-700 dropdown-toggle d-flex align-items-center gap-2 px-3 py-2 rounded-3"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{
                    color: "rgba(255,255,255,0.75)",
                    transition: "all 0.25s ease",
                    fontSize: "0.9rem",
                  }}
                >
                  <FiScissors size={15} /> Tools
                </a>
                <ul
                  className="dropdown-menu border-0 p-3 mt-2"
                  style={{
                    background: "rgba(10, 15, 30, 0.97)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "18px",
                    boxShadow: "0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)",
                    minWidth: "220px",
                  }}
                >
                  {toolLinks.map((item) => (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className="dropdown-item rounded-3 py-2 d-flex align-items-center gap-3 fw-bold"
                        style={{ color: "rgba(255,255,255,0.85)", transition: "all 0.2s" }}
                        onClick={() => setIsMenuOpen(false)}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.07)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <div
                          style={{
                            background: `${item.color}22`,
                            color: item.color,
                            padding: "8px",
                            borderRadius: "10px",
                            display: "flex",
                          }}
                        >
                          {item.icon}
                        </div>
                        <div>
                          <div style={{ fontSize: "0.9rem" }}>{item.label}</div>
                          <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", fontWeight: 500 }}>{item.desc}</div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>

              {/* Auth Buttons */}
              {!isAuthenticated ? (
                <li className="nav-item ms-2">
                  <div className="d-flex align-items-center gap-2">
                    <Link
                      to="/signin"
                      className="nav-link fw-700 px-3 py-2 rounded-3"
                      style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.9rem" }}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/signup"
                      className="fw-800 px-4 py-2 text-decoration-none"
                      style={{
                        background: "linear-gradient(135deg, #4F46E5, #E11D48)",
                        color: "white",
                        borderRadius: "50px",
                        fontSize: "0.88rem",
                        boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
                        transition: "all 0.3s ease",
                        display: "inline-block",
                      }}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Get Started ✦
                    </Link>
                  </div>
                </li>
              ) : (
                <li className="nav-item dropdown ms-2">
                  <div
                    className="d-flex align-items-center gap-2 cursor-pointer"
                    id="userDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{
                      background: "rgba(255,255,255,0.07)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: "50px",
                      padding: "6px 14px 6px 6px",
                      cursor: "pointer",
                      transition: "all 0.25s ease",
                    }}
                  >
                    <div
                      style={{
                        background: "linear-gradient(135deg, #4F46E5, #E11D48)",
                        color: "white",
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 900,
                        fontSize: "0.9rem",
                        boxShadow: "0 0 12px rgba(79,70,229,0.5)",
                      }}
                    >
                      {user?.username?.charAt(0).toUpperCase() || <FiUser size={16} />}
                    </div>
                    <div>
                      <div style={{ color: "white", fontWeight: 800, fontSize: "0.82rem", lineHeight: 1.2 }}>
                        {user?.username || "User"}
                      </div>
                      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.05em" }}>
                        PRO
                      </div>
                    </div>
                    <FiChevronDown size={14} color="rgba(255,255,255,0.4)" />
                  </div>

                  <ul
                    className="dropdown-menu dropdown-menu-end border-0 p-3 mt-2"
                    style={{
                      background: "rgba(10, 15, 30, 0.97)",
                      backdropFilter: "blur(20px)",
                      borderRadius: "18px",
                      boxShadow: "0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)",
                      minWidth: "220px",
                    }}
                    aria-labelledby="userDropdown"
                  >
                    <div className="px-3 py-2 mb-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                      <div style={{ color: "white", fontWeight: 800 }}>{user?.username}</div>
                      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>{user?.email}</div>
                    </div>
                    <li>
                      <button
                        className="dropdown-item rounded-3 fw-800 py-2 d-flex align-items-center gap-3"
                        style={{ color: "#F87171", background: "transparent", transition: "all 0.2s" }}
                        onClick={handleLogout}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(248,113,113,0.1)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <div style={{ background: "rgba(248,113,113,0.15)", padding: "7px", borderRadius: "9px", display: "flex" }}>
                          <FiLogOut size={15} color="#F87171" />
                        </div>
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Mobile Fullscreen Overlay */}
        <style>{`
          .nav-link:hover {
            color: #818CF8 !important;
            background: rgba(79,70,229,0.12) !important;
          }
          @media (max-width: 991px) {
            .navbar-collapse {
              position: fixed;
              top: 0; left: 0; right: 0; bottom: 0;
              height: 100vh;
              background: rgba(2, 6, 23, 0.97);
              backdrop-filter: blur(24px);
              padding: 100px 30px;
              z-index: -1;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
              opacity: 0;
              visibility: hidden;
              transform: translateY(-10px);
            }
            .navbar-collapse.show {
              opacity: 1;
              visibility: visible;
              transform: translateY(0);
            }
            .navbar-nav { width: 100%; text-align: center; }
            .nav-item { margin-bottom: 10px; }
            .nav-link { font-size: 1.3rem; justify-content: center; }
            .dropdown-menu {
              position: static !important;
              background: rgba(255,255,255,0.05) !important;
              box-shadow: none !important;
              text-align: center;
              transform: none !important;
            }
          }
        `}</style>
      </nav>
    </>
  );
};
